import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'

import { richTextToPlainText } from 'utils/richText'
import { useAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useAnalysisTypeGenerating } from 'redux/hooks/AnalysisGenerations'
import { useParams } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'

import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import Hero from 'components_new/molecules/Hero'
import NavigationItem from 'components_new/molecules/NavigationItem'
import LoadingSection from 'components_new/molecules/LoadingSection'
import SectionHeader from 'components_new/molecules/SectionHeader'
import StatusBox from 'components_new/molecules/StatusBox'
import SwitchItem from 'components_new/molecules/SwitchItem'

import AnalysisDialog from 'components_new/organisms/dialogs/AnalysisDialog'
import AnalysesTable from 'components_new/organisms/analyses/AnalysesTable'
import AnalysisTypeSummary from 'components_new/organisms/analysis_types/AnalysisTypeSummary'

import AnalysisTypeTemplate from 'components_new/templates/AnalysisTypeTemplate'
import PageLayout from 'components_new/templates/PageLayout'

import { canRun, getInvestigationDashboardLocation } from 'utils/analysis'

import { useAnalysisTypeActions } from 'redux/actions/AnalysisTypes'
import { useDashboardGroup } from 'redux/hooks/DashboardGroups'

const AnalysisType = () => {
  const { id } = useParams<{ id: string }>()

  const { analysisType, error, loading } = useAnalysisType(id)
  const generating = useAnalysisTypeGenerating()
  const history = useHistory()
  const { t } = useTranslation()

  const { tryUpdateAnalysisTypeSettings } = useAnalysisTypeActions()
  const investigationDashboard = useDashboardGroup(
    analysisType?.dashboard_group_id ?? null
  )
  const investigationDashboardId =
    investigationDashboard?.firstDashboardId ?? null

  const [dialog, setDialog] = useState<{ open: boolean }>({
    open: false
  })
  const [activating, setActivating] = useState<boolean>(false)

  const handleCloseDialog = () => {
    setDialog({
      open: false
    })
  }

  const handleActivate = () => {
    if (!analysisType || activating) {
      return
    }

    // Let the switch animate to its new state before the update closes
    // the status box.
    setActivating(true)

    setTimeout(() => {
      tryUpdateAnalysisTypeSettings(
        analysisType.id,
        { active: true },
        (err) => {
          if (err) {
            setActivating(false)
          }
        }
      )
    }, 300)
  }

  const handleOpenDialog = () => {
    setDialog({
      open: true
    })
  }

  if (error || (!loading && !analysisType)) {
    return (
      <PageLayout
        topbar={{
          breadcrumbs: [
            {
              href: '/analysis-types',
              icon: { name: 'AnalysisTypes' },
              title: t('analysis_types', 'analysis_types')
            }
          ]
        }}
        variant="static"
      >
        <ErrorPlaceholder
          actions={
            <Button
              onClick={() => history.push('/analyses')}
              startIcon={<Icon name="ArrowBackOutlined" />}
              variant="text"
            >
              {t('analysis_types', 'analysis_types')}
            </Button>
          }
          body={error ? undefined : t('analysis_types', 'error_not_found_body')}
          code={error ? 500 : 404}
        />
      </PageLayout>
    )
  }

  if (!analysisType) {
    return (
      <AnalysisTypeTemplate variant="static">
        <LoadingSection />
      </AnalysisTypeTemplate>
    )
  }

  return (
    <>
      <AnalysisTypeTemplate variant="feed">
        <Collapse in={analysisType.status === 'INACTIVE'} unmountOnExit={true}>
          <StatusBox
            actions={
              <SwitchItem
                checked={analysisType.active || activating}
                label={t('global', 'activate')}
                labelPlacement={'start'}
                onChange={handleActivate}
              />
            }
            allowClose={false}
            color="brand"
            severity="success"
            sx={{ mb: 2 }}
            title={t('analysis_types', 'activate_analysis_type') + '?'}
          >
            {t('analysis_types', 'activated_analysis_type_body')}
          </StatusBox>
        </Collapse>

        <Hero
          actions={
            <Button
              disabled={!canRun(analysisType)}
              loading={!!generating[analysisType.id]}
              loadingTitle={t('analysis_types', 'run_loading_title')}
              onClick={handleOpenDialog}
              startIcon={<Icon name="PlayCircleFilledWhite" />}
            >
              {t('analysis_types', 'run_analysis')}
            </Button>
          }
          background={true}
          body={richTextToPlainText(analysisType.description)}
          icon={{ name: 'AnalysisType' }}
          // Hidden when unlinked or the user cannot see the dashboard.
          metaItems={
            investigationDashboard && investigationDashboardId
              ? [
                  {
                    content: (
                      <NavigationItem
                        arrow={true}
                        border={true}
                        color="primary"
                        item={{
                          icon: { name: 'Dashboards' },
                          onClick: () =>
                            history.push(
                              getInvestigationDashboardLocation(
                                investigationDashboardId,
                                null
                              )
                            ),
                          title: investigationDashboard.title
                        }}
                      />
                    ),
                    label: t('analyses', 'investigated_in')
                  }
                ]
              : undefined
          }
          size="medium"
          sx={{
            filter:
              analysisType.status !== 'ACTIVE' ? 'grayscale(1)' : undefined
          }}
          title={analysisType.name}
        />

        <AnalysisTypeSummary context="page" id={id} sx={{ mb: 4 }} />

        <SectionHeader
          divider={true}
          title={t('analysis_types', 'latest_analyses')}
        />
        <AnalysesTable
          analysisTypeId={id}
          preview={true}
          viewAllHref={`/analysis-types/${id}/analyses`}
        />
      </AnalysisTypeTemplate>

      {/*-- dialogs --*/}
      <AnalysisDialog id={id} onClose={handleCloseDialog} open={dialog.open} />
    </>
  )
}

export default AnalysisType
